import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import CustomButton from "../components/common/CustomButton";
import CustomLabel from "../components/common/CustomLabel";
import {
  Container,
  GlobalReset,
  LeftPanel,
  OuterContainer,
  RightPanel,
} from "../components/layout/CommonLoginLayout";
import { COLORS } from "../assets/theme";
import ToastNotification from "../components/ToastNotification";
import { TwoFactorMethod, userOtpSelect } from "../services/AuthService";

type PendingOtpSession = {
  uuid: string;
  availableMethods: TwoFactorMethod[];
  method?: TwoFactorMethod;
  otpExpiresInSeconds?: number;
};

const PENDING_OTP_SESSION_KEY = "pendingOtpSession";

function readPendingOtpSession(): PendingOtpSession | null {
  const raw = sessionStorage.getItem(PENDING_OTP_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingOtpSession>;
    if (!parsed.uuid) return null;
    const methods = Array.isArray(parsed.availableMethods)
      ? (parsed.availableMethods.filter(Boolean) as TwoFactorMethod[])
      : [];
    return {
      uuid: parsed.uuid,
      availableMethods: methods,
      method: parsed.method,
      otpExpiresInSeconds: parsed.otpExpiresInSeconds,
    };
  } catch {
    return null;
  }
}

function writePendingOtpSession(next: PendingOtpSession) {
  sessionStorage.setItem(PENDING_OTP_SESSION_KEY, JSON.stringify(next));
}

function formatMethod(method: TwoFactorMethod) {
  if (method === TwoFactorMethod.EMAIL) return "Email";
  if (method === TwoFactorMethod.TOTP) return "Authenticator";
  if (method === TwoFactorMethod.WHATSAPP) return "WhatsApp";
  return method;
}

const ChooseTwoFactorMethod = () => {
  const [accessToken] = useState(localStorage.getItem("access_token"));
  if (accessToken) return <Navigate to="/" />;
  return <UnAuthed />;
};

const UnAuthed = () => {
  const navigate = useNavigate();
  const session = useMemo(() => readPendingOtpSession(), []);
  const [submitting, setSubmitting] = useState(false);

  const availableMethods = useMemo(
    () => session?.availableMethods ?? [],
    [session?.availableMethods]
  );
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod | null>(
    availableMethods[0] ?? null
  );

  useEffect(() => {
    if (!session?.uuid) {
      ToastNotification.info("Please login first.");
      navigate("/login", { replace: true });
      return;
    }
    if (availableMethods.length > 0 && !selectedMethod) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, navigate, selectedMethod, session?.uuid]);

  const handleContinue = async () => {
    if (!session?.uuid) return;
    if (!selectedMethod) {
      ToastNotification.error("Please choose a two-factor method.");
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await userOtpSelect(session.uuid, selectedMethod);
      const otpMsg = res?.message || "Code has been sent. Continue to verification.";
      const otpExpires = res?.otpExpiresInSeconds;
      let infoMsg = otpMsg;
      if (otpExpires) infoMsg += `  ${otpExpires}s.`;
      ToastNotification.success(infoMsg, { timeOut: 10000 });

      writePendingOtpSession({
        uuid: session.uuid,
        availableMethods,
        method: selectedMethod,
        otpExpiresInSeconds: otpExpires,
      });

      navigate("/login-otp");
    } catch (e: any) {
      ToastNotification.error(e?.message || "Failed to send code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Outlet />
      <GlobalReset />
      <OuterContainer>
        <Container>
          <RightPanel>
            <CustomLabel
              text="Choose Two-Factor Method"
              fontWeight="bold"
              fontSize="24px"
              color="#A39455"
              sx={{ marginBottom: 2, textAlign: "left", width: "100%" }}
            />

            <CustomLabel
              text="Select how you want to receive your one-time code."
              fontSize="14px"
              color="#4A4A4A"
              sx={{ marginBottom: 3, textAlign: "left", width: "100%" }}
            />

            <Box sx={{ width: "100%", marginBottom: 3 }}>
              <CustomLabel
                text="Available methods"
                fontSize="14px"
                fontWeight="bold"
                color="#4A4A4A"
                sx={{ marginBottom: 1, textAlign: "left", width: "100%" }}
              />
              <ToggleButtonGroup
                value={selectedMethod}
                exclusive
                onChange={(_, next) => setSelectedMethod(next)}
                sx={{
                  width: "100%",
                  display: "flex",
                  gap: 1,
                  "& .MuiToggleButtonGroup-grouped": {
                    margin: 0,
                    borderRadius: "20px !important",
                    flexWrap: "wrap",
                    padding: "5px 0px !important",
                  },
                }}
              >
                {availableMethods.map((m) => (
                  <ToggleButton
                    key={m}
                    value={m}
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      fontWeight: 700,
                      border: "1px solid #A39455 !important",
                      backgroundColor: "#f5f5f5",
                      color: "#6b6b6b",
                      "&:hover": { backgroundColor: "#D6D6D6" },
                      "&.Mui-selected": {
                        backgroundColor: "#A39455",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#8E824B" },
                      },
                    }}
                  >
                    {formatMethod(m)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                justifyContent: "space-between",
                marginTop: 2,
              }}
            >

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <CustomButton
                  label="Back to Login"
                  onClick={() => {
                    sessionStorage.removeItem(PENDING_OTP_SESSION_KEY);
                    navigate("/login");
                  }}
                  bgColor="#7A7A7A"
                  textColor="#fff"
                  borderRadius="4px"
                  size="medium"
                  sx={{ height: "40px", width: "100%" }}
                  disabled={submitting}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <CustomButton
                  label={submitting ? "Sending..." : "Continue"}
                  onClick={handleContinue}
                  bgColor="#A39455"
                  textColor="#fff"
                  borderRadius="4px"
                  size="medium"
                  sx={{ height: "40px", width: "100%" }}
                  disabled={submitting || !selectedMethod}
                />
              </Box>
            </Box>
          </RightPanel>

          <LeftPanel
            sx={{
              maxHeight: { xs: "80vh" },
              pt: { xs: 6, sm: 7, md: 12 },
            }}
          >
            <Box
              component="img"
              src="/SPARK LOGO-white.png"
              alt="Logo"
              sx={{
                width: { xs: "200px", sm: "250px", md: "300px" },
                height: "auto",
                marginBottom: { xs: "10px", md: "20px" },
              }}
            />

            <CustomLabel
              text="Secure Two-Factor Authentication"
              sx={{
                fontSize: { xs: "14px", sm: "16px", md: "17px" },
                textAlign: "center",
              }}
              color={COLORS.GOLD}
              marginTop="40px"
              fontWeight="bold"
            />

            <CustomLabel
              text="Choose the best method for you, then enter the code to complete login."
              sx={{
                fontSize: { xs: "12px", sm: "14px", md: "15px" },
                fontWeight: 400,
                lineHeight: "1.5",
                textAlign: "center",
                maxWidth: { xs: "80%", md: "600px" },
                color: "white",
                margin: "0 auto",
              }}
            />

            <CustomLabel
              text="Powered by Teways"
              color="#fff"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "6px", sm: "7px", md: "8px" },
              }}
            />
          </LeftPanel>
        </Container>
      </OuterContainer>
    </>
  );
};

export default ChooseTwoFactorMethod;


